import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CATEGORIES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const cat = CATEGORIES.find((c) => c.id === categoryId);
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/articles`, { params: { category: categoryId } })
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]));
  }, [categoryId]);

  return (
    <main
      className="min-h-screen bg-samatva-cream text-samatva-ink font-sans"
      data-testid="category-page"
    >
      <div className="noise-overlay" />
      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <Link
          to="/"
          data-testid="category-back-link"
          className="inline-flex items-center gap-2 text-sm text-samatva-muted hover:text-samatva-plum transition-colors duration-300 mb-12"
        >
          <ArrowLeft size={16} /> Back to Samatva
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-samatva-plum mb-4">
            Samatva Stories
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.05]">
            {cat ? cat.title : categoryId}
            {cat && cat.hindi && (
              <span className="text-samatva-plum/50 italic text-3xl md:text-4xl ml-4">
                {cat.hindi}
              </span>
            )}
          </h1>
        </motion.div>

        <div className="mt-14">
          {articles && articles.length === 0 && (
            <motion.p
              data-testid="category-empty-state"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="font-serif text-2xl md:text-4xl font-light leading-snug text-samatva-ink/60 max-w-xl"
            >
              Stories are being written. This space will fill{" "}
              <em className="text-samatva-plum">soon.</em>
            </motion.p>
          )}

          {(articles || []).map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/column/${a.slug}`}
                data-testid={`category-article-${i + 1}`}
                className="group flex gap-6 items-center border-b border-samatva-line py-8 hover:bg-samatva-peach/30 transition-colors duration-300 -mx-4 px-4 rounded-lg"
              >
                {a.cover_image && (
                  <img
                    src={a.cover_image}
                    alt={a.title}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-xl md:text-3xl font-light text-samatva-ink group-hover:text-samatva-plum transition-colors duration-300">
                      {a.title}
                    </h3>
                    <ArrowRight
                      size={20}
                      className="shrink-0 text-samatva-plum opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                    />
                  </div>
                  {a.subtitle && (
                    <p className="mt-1 font-serif italic text-samatva-plum/80">
                      {a.subtitle}
                    </p>
                  )}
                  <p className="mt-2 text-xs tracking-[0.2em] uppercase text-samatva-muted">
                    {a.author ? `By ${a.author}` : ""}
                    {a.author && a.publish_date ? " · " : ""}
                    {a.publish_date || ""}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
